import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import CommonPopup from "Components/common/CommonPopup";
import CreateOrEditSchedules from "Pages/Dashboard/settings/Schedules/CreateOrEditSchedules";
import scheduleDataStore from "Stores/ScheduleData";
import userDetail from "Stores/userDetail";
import { getGlobalTimeZones } from "Utils/commonAPI";

// Inline create/edit schedule modal for the sequence flow. Embeds the settings
// CreateOrEditSchedules form unmodified; a newly created schedule is detected
// by diffing schedule ids after the form's own store refetch completes, an
// edited one by re-reading its id from the refreshed store.
const CreateSchedulePopup = observer((props = {}) => {
  const {
    show = false,
    onClose = () => {},
    onCreated = () => {},
    onUpdated = () => {},
    editItem = null
  } = props || {};

  const beforeIdsRef = useRef(new Set());
  const [pendingDetect, setPendingDetect] = useState(false);
  // "Create a new schedule instead" flips an edit session into create mode.
  const [forceCreate, setForceCreate] = useState(false);

  const effectiveEditItem = forceCreate ? null : editItem;
  const isEditMode = !!effectiveEditItem?.id;

  useEffect(() => {
    if (show) {
      beforeIdsRef.current = new Set(
        (scheduleDataStore?.scheduleData?.data || []).map(item => item?.id)
      );
      setPendingDetect(false);
      setForceCreate(false);
      if (!userDetail?.globalTimeZone?.length) {
        getGlobalTimeZones();
      }
    }
  }, [show, editItem]);

  useEffect(() => {
    if (pendingDetect && !scheduleDataStore?.scheduleDataLoading) {
      const list = scheduleDataStore?.scheduleData?.data || [];
      const created = list.find(
        item => item?.id && !beforeIdsRef.current.has(item.id)
      );
      if (created) {
        setPendingDetect(false);
        onCreated(created);
      } else if (effectiveEditItem?.id) {
        const updated = list.find(item => item?.id === effectiveEditItem.id);
        if (updated) {
          setPendingDetect(false);
          onUpdated(updated);
        }
      }
    }
  }, [scheduleDataStore.scheduleData, pendingDetect]);

  const handleHide = () => {
    // The embedded form calls hideCbk on both cancel and save; on save it flips
    // scheduleDataLoading right after. Defer so we can tell the two apart.
    setTimeout(() => {
      if (scheduleDataStore?.scheduleDataLoading) {
        setPendingDetect(true);
      }
      onClose();
    }, 0);
  };

  if (!show) {
    return <></>;
  }

  return (
    <CommonPopup
      headerTitle={isEditMode ? "Edit schedule" : "Create new schedule"}
      className="inlineSchedulePopup"
      closeIconCbk={onClose}
    >
      {isEditMode && (
        <div className="sharedScheduleNote">
          <i className="material-icons">info_outline</i>
          <span>
            This schedule is shared. Changes apply to every sequence using it.
          </span>
          <button
            type="button"
            className="createInsteadBtn"
            onClick={() => setForceCreate(true)}
          >
            Create a new schedule instead
          </button>
        </div>
      )}
      <CreateOrEditSchedules
        key={isEditMode ? `edit-${effectiveEditItem?.id}` : "create"}
        hideCbk={handleHide}
        item={effectiveEditItem || {}}
        isFromEdit={isEditMode}
      />
    </CommonPopup>
  );
});

export default CreateSchedulePopup;
export { CreateSchedulePopup };
